<template>
  <section class="ingredients-details-section ws-grid">
    <div
      class="ingredients-details-section__swiper swiper ingredients-details-section__content ws-col-8-gte960 ws-col-5-gte1200"
    >
      <swiper-type-three v-if="slides" ref="swiper" :items="slides">
        <template v-slot:item="{ item }">
          <img class="swiper__slide" :src="item.image" :alt="item.name" loading="lazy" />
        </template>
      </swiper-type-three>

      <div class="swiper__subtitle">{{ $t('section.yourTreatment.image.subtitle') }}</div>
    </div>

    <div
      class="ingredients-details-section__details details ingredients-details-section__content ws-col-8-gte960 ws-col-6-gte1200 ws-col-5-gte1440 ws-col-4-gte1920"
    >
      <div class="details__subtitle u-typography-helvetica u-text u-text--m">
        {{ $t('action.whatInside') }}
      </div>
      <h2 class="details__title u-typography-helvetica u-text-display u-text-display--s">
        {{ $t('discover.product.serum.moreIngredients') }}
      </h2>

      <app-tabs
        v-if="selectedTab"
        :key="locale"
        :selected-item-id="selectedTab.id"
        :items="tabs"
        @select="onTabSelect"
      >
        <template v-slot:tab-title="{ item }">
          <div class="tab-title">
            <app-icon v-if="item.iconName" class="tab-title__icon" :name="item.iconName" />
            <span class="tab-title__text">{{ $t(item.title) }}</span>
          </div>
        </template>

        <template v-slot:default>
          <serum-ingredients-tab
            v-if="selectedTab"
            :pack-item="selectedTab.formula"
            :pack-usage="productUsage"
            @ingredient-click="onIngredientSelect"
          />
        </template>
      </app-tabs>

      <div class="details__inci" @click="onShowInciList">
        {{ $t('discover.expandableList.section.seeInciList') }}
      </div>
    </div>

    <sidebar-popup ref="incilistPopup">
      <product-inci-list :product-name="productName" />
    </sidebar-popup>
  </section>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';

import SwiperTypeThree from '@/components/common/app-swiper/SwiperTypeThree.vue';
import AppTabs from '@/components/common/AppTabs.vue';
import SerumIngredientsTab from '@/components/product-subpages/SerumIngredientsTab.vue';
import AppIcon, { ICON_NAME } from '@/components/common/AppIcon.vue';
import SidebarPopup from '@/components/common/SidebarPopup.vue';
import ProductInciList from '@/components/ProductInciList.vue';

import {
  getProductOptionsDefaultOption,
  getProductOptionsOptionByFormulaIdOrDefault,
  getProductOptionsProductPacksList
} from '@/models/core/ProductOptions';
import {
  getProductPackItems,
  getProductPackUsage,
  isProductPackGenericEyeSerum
} from '@/models/core/ProductPack';
import {
  getPackItemIngredients,
  getPackItemName,
  getPackItemUsage,
  getPackItemFormulaId,
  isPackItemEqual
} from '@/models/core/PackItem';
import { getIngredientName } from '@/models/core/Ingredient';

import {
  FORMULA_USAGE,
  SERUM_USAGES,
  EYE_SERUM_GENERIC_FORMULA_LABELS
} from '@/constants/treatment';
import { types } from '@/store/types';

const USAGE_SUFFIX = {
  [SERUM_USAGES.MORNING_ONLY]: 'AM',
  [SERUM_USAGES.EVENING_ONLY]: 'PM',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'AMPM'
};

const USAGE_ICON = {
  [FORMULA_USAGE.MORNING_ONLY]: ICON_NAME.SUN,
  [FORMULA_USAGE.EVENING_ONLY]: ICON_NAME.MOON,
  [FORMULA_USAGE.MORNING_AND_EVENING]: ICON_NAME.SUN_AND_MOON
};

const CUSTOM_FORMULA_DISPLAY_NAME = {
  [FORMULA_USAGE.MORNING_ONLY]: 'discover.product.serum.amSerum',
  [FORMULA_USAGE.EVENING_ONLY]: 'discover.product.serum.pmSerum',
  [FORMULA_USAGE.MORNING_AND_EVENING]: 'discover.product.serum.amPmSerum'
};

const buildSlide = async (name, usage) => {
  const fileName = usage ? `${name}${USAGE_SUFFIX[usage]}.jpg` : `${name}.jpg`;

  return {
    id: name,
    image: (await import(`@/assets/images/product-subpage/${fileName}`)).default
  };
};

const buildTab = async formula => {
  const formulaSlide = await buildSlide(getPackItemName(formula), getPackItemUsage(formula));
  const ingredients = getPackItemIngredients(formula);
  const ingredientSlides = await Promise.all(
    ingredients.map(ingredient => {
      const name = getIngredientName(ingredient);

      return buildSlide(name);
    })
  );

  const genericFormulaId = getPackItemFormulaId(formula);
  const formulaUsage = getPackItemUsage(formula);
  const title = genericFormulaId
    ? EYE_SERUM_GENERIC_FORMULA_LABELS[genericFormulaId]
    : CUSTOM_FORMULA_DISPLAY_NAME[formulaUsage];

  return {
    id: genericFormulaId || formulaUsage,
    title,
    ingredients,
    usage: formulaUsage,
    formula,
    slides: [formulaSlide, ...ingredientSlides],
    iconName: genericFormulaId ? '' : USAGE_ICON[formulaUsage]
  };
};

export default {
  name: 'SerumIngredientsDetailsSection',
  components: {
    SwiperTypeThree,
    AppTabs,
    SerumIngredientsTab,
    AppIcon,
    SidebarPopup,
    ProductInciList
  },
  props: {
    productName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedTab: null,
      tabs: null
    };
  },
  computed: {
    ...mapState({
      selectedEyeContourGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID,
      locale: types.state.LOCALE
    }),
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS
    }),
    isEyeSerumGeneric() {
      const defaultOption = getProductOptionsDefaultOption(
        this.allProductOptions[this.productName]
      );

      return isProductPackGenericEyeSerum(defaultOption);
    },
    product() {
      const productOptions = this.allProductOptions[this.productName];

      if (this.isEyeSerumGeneric) {
        return getProductOptionsOptionByFormulaIdOrDefault(
          this.selectedEyeContourGenericFormulaId,
          productOptions
        );
      }

      return getProductOptionsDefaultOption(productOptions);
    },
    productUsage() {
      return getProductPackUsage(this.product);
    },
    selectedTabIngredients() {
      return getPackItemIngredients(this.selectedTab.formula);
    },
    selectedTabBottleUsage() {
      return getPackItemUsage(this.selectedTab.formula);
    },
    formulas() {
      if (this.isEyeSerumGeneric) {
        return getProductOptionsProductPacksList(this.allProductOptions[this.productName]).flatMap(
          getProductPackItems
        );
      }

      return getProductPackItems(this.product).reduce((acc, packItem) => {
        const doesAlreadyIncludeTheItem = !!acc.find(item => isPackItemEqual(item, packItem));

        if (doesAlreadyIncludeTheItem) {
          return acc;
        }

        return [...acc, packItem];
      }, []);
    },
    slides() {
      if (!this.selectedTab) {
        return null;
      }

      return this.selectedTab.slides;
    }
  },
  watch: {
    selectedEyeContourGenericFormulaId: 'onGenericFormulaChange'
  },
  created() {
    this.setTabs();
  },
  methods: {
    ...mapMutations({
      setSelectedEyeSerumGenericFormulaId: types.mutations.SET_SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    async setTabs() {
      this.tabs = await Promise.all(this.formulas.map(buildTab));
      const [firstTab] = this.tabs;

      if (this.isEyeSerumGeneric && this.selectedEyeContourGenericFormulaId) {
        this.selectedTab = this.tabs.find(
          tab => tab.id === this.selectedEyeContourGenericFormulaId
        );

        return;
      }

      this.selectedTab = firstTab;
    },
    changeSwiperPage(page) {
      this.$refs.swiper.navigateToPage(page);
    },
    onIngredientSelect(ingredientName) {
      const page = this.slides.findIndex(slide => slide.id === ingredientName);
      this.changeSwiperPage(page);
    },
    onGenericFormulaChange(genericFormulaId) {
      if (!genericFormulaId) {
        return;
      }

      this.onTabSelect(genericFormulaId);
    },
    onTabSelect(tabId) {
      if (!this.tabs) {
        return;
      }

      this.selectedTab = this.tabs.find(({ id }) => id === tabId);

      const page = this.slides.findIndex(slide => slide.id === tabId);
      this.changeSwiperPage(page);

      if (this.isEyeSerumGeneric) {
        this.setSelectedEyeSerumGenericFormulaId(tabId);
      }
    },
    onShowInciList() {
      this.$refs.incilistPopup.open();
    }
  }
};
</script>

<style lang="scss" scoped>
.ingredients-details-section {
  font-family: var(--u-font-family-Helvetica);
  padding: 32px var(--ws-grid-m, 16px) 48px var(--ws-grid-m, 16px);

  &__content {
    max-width: 508px;
    margin-inline-start: auto;
    margin-inline-end: auto;
  }
}

.swiper {
  &__slide {
    width: 100%;
  }

  &__subtitle {
    font-size: 14px;
    line-height: 18px;
    color: var(--u-color-grey-600);
    margin-top: 16px;
    margin-bottom: 8px;
    text-align: center;
  }
}

.details {
  &__subtitle {
    color: var(--u-color-grey-900);
    margin-bottom: 16px;
  }

  &__title {
    margin-bottom: 32px;
  }

  &__inci {
    font-family: var(--u-font-family-Gilroy);
    font-weight: 600;
    font-size: 16px;
    line-height: 32px;
    letter-spacing: 0.4px;
    text-decoration-line: underline;
    color: var(--u-color-grey-700);
    cursor: pointer;
    margin-top: 16px;
  }
}

.tab-title {
  display: flex;
  justify-content: center;
  align-items: center;

  &__icon {
    margin-inline-end: 10px;
  }

  &__text {
    max-width: 50vw;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

@media (min-width: 960px) {
  .ingredients-details-section {
    &__content {
      max-width: unset;
    }
  }
}

@media (min-width: 1200px) {
  .ingredients-details-section {
    display: flex;
    padding-top: 100px;
    padding-bottom: 100px;

    &__content {
      max-width: unset;
      margin-inline-start: 0;
      margin-inline-end: 0;
    }

    &__swiper {
      margin-inline: 0;
    }

    &__details {
      margin-top: 40px;
      margin-inline-start: calc(var(--ws-grid-g) * 2 + var(--ws-grid-col-size));
    }
  }

  .swiper {
    &__subtitle {
      margin-top: -12px;
      text-align: start;
    }
  }

  .details {
    &__title {
      margin-bottom: 40px;
    }

    &__inci {
      margin-top: 40px;
    }
  }
}
</style>
